import { useQuery } from '@tanstack/react-query';
import { fetchHealth } from '../api/health';

/**
 * Live backend status widget — the Phase 0 health indicator, folded into the
 * Phase 1 shells (login page + workspace overview). Polls `GET /api/health`
 * and renders the parsed payload; failures render a visible alert, never a
 * blank spot (§10).
 */
export function HealthBadge(): React.JSX.Element {
  const health = useQuery({
    queryKey: ['health'],
    queryFn: ({ signal }) => fetchHealth({ signal }),
    refetchInterval: 30_000,
  });

  if (health.isPending) {
    return (
      <p role="status" className="text-sm text-slate-500">
        Checking backend health…
      </p>
    );
  }

  if (health.isError) {
    return (
      <p role="alert" className="text-sm text-red-600">
        Backend unreachable:{' '}
        {health.error instanceof Error ? health.error.message : 'Failed to reach backend'}
      </p>
    );
  }

  const { data } = health;
  return (
    <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
      <dt className="text-slate-500">Status</dt>
      <dd data-testid="health-ok" className="font-medium text-slate-900">
        {data.ok ? 'healthy' : 'degraded'}
      </dd>
      <dt className="text-slate-500">Database</dt>
      <dd data-testid="health-db">{data.db}</dd>
      <dt className="text-slate-500">Version</dt>
      <dd data-testid="health-version">{data.version}</dd>
      <dt className="text-slate-500">Commit</dt>
      <dd data-testid="health-commit">{data.commit}</dd>
    </dl>
  );
}
